import React, { useCallback, useState } from 'react';
import { Box, Stack, Typography } from '@mui/material';
import Article from '@mui/icons-material/Article';
import Description from '@mui/icons-material/Description';
import InsertDriveFileOutlined from '@mui/icons-material/InsertDriveFileOutlined';
import PictureAsPdf from '@mui/icons-material/PictureAsPdf';
import TableChart from '@mui/icons-material/TableChart';
import { documentType, resolveFileUrl } from './documentUtils';
import DocumentViewer from './DocumentViewer';
import ImageLightbox from './ImageLightbox';
import { MainPreview } from './BusPhotoGallery';

const ACCENT = '#2563EB';
const BORDER = '#E5EAF2';
const typeIcons = { pdf: [PictureAsPdf, '#DC2626'], word: [Article, '#2563EB'], spreadsheet: [TableChart, '#15803D'], file: [Description, '#64748B'] };
const DEFAULT_TEXT = {
  missing: 'Not uploaded', broken: 'Image unavailable', image: 'Image', pdf: 'PDF document', word: 'Word document', spreadsheet: 'Spreadsheet', file: 'File attachment',
  enlarge: 'Click the preview to enlarge', uploaded: (count, total) => `${count} of ${total} uploaded`,
};

function isMissing(path) {
  return path == null || (typeof path === 'string' && (!path.trim() || /^(null|undefined|false)$/i.test(path.trim())));
}

// documents: [{ key, label, path, meta? }]. Images load straight into <img>/<object>,
// never through fetch, so cross-origin file hosts cannot block the preview.
export default function DocumentGallery({ documents, text: textOverrides, actionLabels, itemName = 'document' }) {
  const text = { ...DEFAULT_TEXT, ...textOverrides };
  const [failed, setFailed] = useState(() => new Set());
  const items = documents.map(doc => {
    const url = resolveFileUrl(doc.path);
    const kind = !url ? 'missing' : documentType(url);
    return { ...doc, url, kind: kind === 'image' && failed.has(url) ? 'broken' : kind, invalid: !url && !isMissing(doc.path) };
  });
  const [selectedKey, setSelectedKey] = useState(() => (items.find(item => item.kind !== 'missing') || items[0])?.key);
  const [viewerOpen, setViewerOpen] = useState(false);
  const selected = items.find(item => item.key === selectedKey) || items[0];
  const images = items.filter(item => item.kind === 'image');
  const imageIndex = Math.max(0, images.findIndex(item => item.key === selected?.key));
  const markFailed = useCallback(url => setFailed(previous => previous.has(url) ? previous : new Set(previous).add(url)), []);
  const step = delta => { if (images.length > 1) setSelectedKey(images[(imageIndex + delta + images.length) % images.length].key); };
  if (!selected) return null;

  const uploaded = items.filter(item => item.kind !== 'missing').length;
  return (
    <Box sx={{ display: 'grid', minWidth: 0, columnGap: 2, rowGap: 1.5, gridTemplateColumns: { xs: 'minmax(0, 1fr)', md: 'minmax(0, 5fr) minmax(0, 7fr)' }, gridTemplateAreas: { xs: '"preview" "thumbs"', md: '"thumbs preview"' } }}>
      <Box sx={{ gridArea: 'preview', minWidth: 0, minHeight: { md: 260 }, display: 'flex', flexDirection: 'column' }}>
        {selected.kind === 'image' ? (
          <Box sx={{ flex: 1, display: 'flex', '& > button': { height: { xs: 240, md: '100%' } } }}>
            <MainPreview key={selected.url} photo={selected} label={selected.label} onOpen={() => setViewerOpen(true)} onError={() => markFailed(selected.url)} />
          </Box>
        ) : selected.kind === 'missing' || selected.kind === 'broken' ? (
          <Stack alignItems="center" justifyContent="center" spacing={.5} sx={{ flex: 1, minHeight: 180, bgcolor: '#F4F7FB', border: `1px dashed ${BORDER}`, borderRadius: 2, p: 1.5, textAlign: 'center' }}>
            <InsertDriveFileOutlined color="disabled" />
            <Typography variant="body2" fontWeight={600}>{selected.label}</Typography>
            <Typography variant="caption" color="text.secondary">{selected.kind === 'broken' ? text.broken : selected.invalid ? 'Invalid attachment path' : text.missing}</Typography>
          </Stack>
        ) : (
          <DocumentViewer key={selected.key} path={selected.path} label={selected.label} height={240} gallery actionLabels={actionLabels} />
        )}
      </Box>

      <Box sx={{ gridArea: 'thumbs', minWidth: 0 }}>
        <Box role="group" aria-label={`${itemName} thumbnails`} sx={{ display: 'grid', gap: 1, gridTemplateColumns: { xs: 'minmax(0, 1fr)', sm: 'repeat(2, minmax(0, 1fr))', md: 'minmax(0, 1fr)' } }}>
          {items.map(item => {
            const active = item.key === selected.key;
            const [Icon, color] = typeIcons[item.kind] || [InsertDriveFileOutlined, '#94A3B8'];
            return (
              <Box key={item.key} component="button" type="button" onClick={() => setSelectedKey(item.key)} aria-label={`Show ${item.label}`} aria-pressed={active}
                sx={{ display: 'flex', alignItems: 'center', gap: 1.25, p: .75, textAlign: 'left', font: 'inherit', color: 'inherit', cursor: 'pointer', bgcolor: active ? '#EFF6FF' : '#fff', borderRadius: 2, border: `1px solid ${active ? ACCENT : BORDER}`, boxShadow: active ? `0 0 0 1px ${ACCENT}` : 'none', transition: 'border-color .15s', '&:hover': { borderColor: active ? ACCENT : '#94A3B8' }, '&:focus-visible': { outline: `2px solid ${ACCENT}`, outlineOffset: 2 } }}>
                <Box sx={{ flex: '0 0 auto', width: 54, aspectRatio: '3 / 4', borderRadius: 1.5, overflow: 'hidden', bgcolor: '#F4F7FB', border: `1px ${item.kind === 'missing' ? 'dashed' : 'solid'} ${BORDER}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  {item.kind === 'image'
                    ? <Box component="img" src={item.url} alt="" onError={() => markFailed(item.url)} sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    : <Icon sx={{ fontSize: 26, color }} />}
                </Box>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={700} lineHeight={1.3} sx={{ overflowWrap: 'anywhere' }}>{item.label}</Typography>
                  {item.meta && <Typography variant="caption" display="block" lineHeight={1.35} sx={{ mt: '2px', overflowWrap: 'anywhere', color: '#334E68' }}>{item.meta}</Typography>}
                  <Typography variant="caption" display="block" color={item.kind === 'missing' || item.kind === 'broken' ? 'text.disabled' : 'text.secondary'} lineHeight={1.35}>{text[item.kind]}</Typography>
                </Box>
              </Box>
            );
          })}
        </Box>
        <Typography variant="caption" color="text.secondary" component="p" mt={.75} lineHeight={1.4}>
          {text.uploaded(uploaded, items.length)}{images.length > 0 && ` · ${text.enlarge}`}
        </Typography>
      </Box>

      <ImageLightbox open={viewerOpen && selected.kind === 'image'} onClose={() => setViewerOpen(false)} images={images.map(item => ({ url: item.url, label: item.label }))}
        index={imageIndex} onStep={step} onError={markFailed} itemName={itemName} />
    </Box>
  );
}
